import '../styles/home.css'
import '../styles/dataCenter.css'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import LandingGlobalHeader from '../components/landing/LandingGlobalHeader'
import LandingFooter from '../components/landing/LandingFooter'
import useLandingAuth from '../components/landing/useLandingAuth'
import { getAreas, getTopics, getIndicators, getCountries } from '../actions/searchActions'
import { buildIndicatorSelectionKey, normalizeTopicName } from '../utils/searchKeys'
import { compareOrderedNames, compareTopicsWithinArea } from '../utils/topicOrdering'
import useDatabaseLastUpdate from '../hooks/useDatabaseLastUpdate'
import useIndicatorMetadataCache from '../hooks/useIndicatorMetadataCache'
import { runConcurrent, DEFAULT_CONCURRENCY } from '../utils/concurrentDownload'

import DataCenterCountryPicker from '../components/dataCenter/DataCenterCountryPicker'
import DataCenterIndicatorTree from '../components/dataCenter/DataCenterIndicatorTree'
import DataCenterUnitsPanel from '../components/dataCenter/DataCenterUnitsPanel'
import DataCenterPeriodPanel from '../components/dataCenter/DataCenterPeriodPanel'
import DataCenterPreviewTable from '../components/dataCenter/DataCenterPreviewTable'
import DataCenterSummary from '../components/dataCenter/DataCenterSummary'

const PREVIEW_DEBOUNCE_MS = 450
const METADATA_DEBOUNCE_MS = 350
const PREVIEW_COUNTRY_LIMIT = 2
const PREVIEW_ROW_LIMIT = 12
const MAX_SHEET_NAME_LENGTH = 31

function sheetSafeName(name, usedNames) {
  let base = name.substring(0, MAX_SHEET_NAME_LENGTH)
  if (base.length === MAX_SHEET_NAME_LENGTH) base = base.substring(0, MAX_SHEET_NAME_LENGTH - 3) + '...'
  let finalName = base
  let counter = 1
  while (usedNames.has(finalName)) {
    const suffix = `_${counter}`
    finalName = base.substring(0, MAX_SHEET_NAME_LENGTH - suffix.length) + suffix
    counter += 1
  }
  usedNames.add(finalName)
  return finalName
}

function DataCenterScreen() {
  const dispatch = useDispatch()
  const location = useLocation()
  const { loadingUser, userInfo, openModal, logoutHandler, authModals } = useLandingAuth()
  const lastUpdateFormatted = useDatabaseLastUpdate()

  useEffect(() => {
    dispatch(getAreas())
    dispatch(getTopics())
    dispatch(getIndicators())
    dispatch(getCountries())
  }, [dispatch])

  const { areaList } = useSelector(state => state.areaList) || {}
  const { topicList } = useSelector(state => state.topicList) || {}
  const { indicatorList } = useSelector(state => state.indicatorList) || {}
  const { countryList } = useSelector(state => state.countryList) || {}

  // ---------------------------------------------------------------
  // Selection state
  // ---------------------------------------------------------------
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedIndicatorKeys, setSelectedIndicatorKeys] = useState([])
  const [frequency, setFrequency] = useState('Annual')
  const [yearFrom, setYearFrom] = useState(1990)
  const [yearTo, setYearTo] = useState(new Date().getFullYear())
  const [uncheckedUnits, setUncheckedUnits] = useState(() => new Set())
  const [uncheckedTransformations, setUncheckedTransformations] = useState(() => new Set())
  const [format, setFormat] = useState('xlsx')
  const [shareMessage, setShareMessage] = useState('')

  const [downloading, setDownloading] = useState(false)
  const [progress, setProgress] = useState(null)

  const [previewRows, setPreviewRows] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)

  const [indicatorAvailability, setIndicatorAvailability] = useState({})
  const [loadingAvailability, setLoadingAvailability] = useState(false)

  const { metadataByIndicator, loading: metadataLoading, ensureMetadata } = useIndicatorMetadataCache()

  // ---------------------------------------------------------------
  // Restore a shared configuration from the URL (?country=&indicator=&
  // frequency=&from=&to=&format=), written by `shareHandler` below.
  // Indicator/country values are applied as-is; the actual objects are
  // resolved later once areaList/topicList/indicatorList/countryList load,
  // so this only has to run once, before those requests resolve.
  // ---------------------------------------------------------------
  const restoredFromUrlRef = useRef(false)
  useEffect(() => {
    if (restoredFromUrlRef.current) return
    restoredFromUrlRef.current = true

    const params = new URLSearchParams(location.search)
    if (Array.from(params.keys()).length === 0) return

    const countries = params.getAll('country')
    const indicators = params.getAll('indicator')
    const sharedFrequency = params.get('frequency')
    const sharedFrom = params.get('from')
    const sharedTo = params.get('to')
    const sharedFormat = params.get('format')

    if (countries.length > 0) setSelectedCountries(countries)
    if (indicators.length > 0) setSelectedIndicatorKeys(indicators)
    if (sharedFrequency) setFrequency(sharedFrequency)
    if (sharedFrom && !Number.isNaN(Number(sharedFrom))) setYearFrom(Number(sharedFrom))
    if (sharedTo && !Number.isNaN(Number(sharedTo))) setYearTo(Number(sharedTo))
    if (sharedFormat) setFormat(sharedFormat)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // ---------------------------------------------------------------
  // Indicator grouping — same ordering criteria as SearchWizard
  // ---------------------------------------------------------------
  const indicatorsWithKeys = useMemo(() => {
    return (indicatorList || []).map(indicator => ({
      ...indicator,
      selectionKey: buildIndicatorSelectionKey(indicator, topicList || []),
    }))
  }, [indicatorList, topicList])

  const indicatorsByKey = useMemo(() => {
    const map = {}
    indicatorsWithKeys.forEach(indicator => { map[indicator.selectionKey] = indicator })
    return map
  }, [indicatorsWithKeys])

  const groupedIndicators = useMemo(() => {
    if (!indicatorsWithKeys || !topicList) return {}

    const areaById = {}
    ;(areaList || []).forEach(a => { areaById[a.id] = { code: a.name, label: a.beauty_name || a.name } })

    const topicById = {}
    ;(topicList || []).forEach(t => {
      const rawName = t.name || ''
      topicById[t.id] = {
        code: rawName,
        label: t.beauty_name || (rawName.includes('_') ? rawName.split('_').slice(1).join('_') : rawName),
        areaId: t.foreign_area,
      }
    })

    const groups = {}
    indicatorsWithKeys.forEach(ind => {
      const topic = topicById[ind.foreign_topic] || { code: '', label: '', areaId: null }
      const areaId = topic.areaId
      const area = areaById[areaId] || { code: 'Other', label: 'Other' }
      const areaKey = areaId ?? 'other'
      const topicKey = ind.foreign_topic ?? 'other'

      if (!groups[areaKey]) groups[areaKey] = { meta: area, topics: {} }
      if (!groups[areaKey].topics[topicKey]) groups[areaKey].topics[topicKey] = { meta: topic, indicators: [] }
      groups[areaKey].topics[topicKey].indicators.push(ind)
    })

    const sorted = {}
    Object.entries(groups)
      .sort(([, a], [, b]) => compareOrderedNames(a.meta.code, b.meta.code, a.meta.label, b.meta.label))
      .forEach(([areaKey, areaData]) => {
        sorted[`area-${areaKey}`] = { meta: areaData.meta, topics: {} }
        Object.entries(areaData.topics)
          .sort(([, a], [, b]) => {
            const areaLabel = areaData.meta.label || areaData.meta.code
            return compareTopicsWithinArea(areaLabel, a.meta.label, b.meta.label, a.meta.code, b.meta.code)
          })
          .forEach(([topicKey, topicData]) => {
            sorted[`area-${areaKey}`].topics[`topic-${topicKey}`] = {
              meta: topicData.meta,
              indicators: topicData.indicators.slice().sort((a, b) => a.name.localeCompare(b.name)),
            }
          })
      })
    return sorted
  }, [indicatorsWithKeys, topicList, areaList])

  const topicNameById = useMemo(() => {
    const map = {}
    ;(topicList || []).forEach(t => { map[t.id] = t.name })
    return map
  }, [topicList])

  const areaNameById = useMemo(() => {
    const map = {}
    ;(areaList || []).forEach(a => { map[a.id] = a.beauty_name || a.name })
    return map
  }, [areaList])

  // Ordered list of selected indicator objects — preserves the user's
  // selection order so the final download is deterministic.
  const selectedIndicators = useMemo(() => (
    selectedIndicatorKeys.map(key => indicatorsByKey[key]).filter(Boolean)
  ), [selectedIndicatorKeys, indicatorsByKey])

  const selectedIndicatorNames = useMemo(() => (
    selectedIndicators.map(ind => ind.name)
  ), [selectedIndicators])

  // ---------------------------------------------------------------
  // Handlers — countries
  // ---------------------------------------------------------------
  const toggleCountry = (name) => {
    setSelectedCountries(prev => (prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]))
  }
  const selectAllCountries = () => setSelectedCountries((countryList || []).map(c => c.name))
  const clearAllCountries = () => setSelectedCountries([])

  // ---------------------------------------------------------------
  // Handlers — indicators
  // ---------------------------------------------------------------
  const toggleIndicator = (selectionKey) => {
    setSelectedIndicatorKeys(prev => (prev.includes(selectionKey) ? prev.filter(k => k !== selectionKey) : [...prev, selectionKey]))
  }

  const toggleUnit = (unit) => {
    setUncheckedUnits(prev => {
      const next = new Set(prev)
      if (next.has(unit)) next.delete(unit)
      else next.add(unit)
      return next
    })
  }
  const toggleTransformation = (transformation) => {
    setUncheckedTransformations(prev => {
      const next = new Set(prev)
      if (next.has(transformation)) next.delete(transformation)
      else next.add(transformation)
      return next
    })
  }

  // ---------------------------------------------------------------
  // Metadata cache — fetch units/transformations for the current
  // indicator x country combinations (debounced, cached, never
  // recomputed).
  // ---------------------------------------------------------------
  const metadataDebounceRef = useRef(null)
  useEffect(() => {
    if (metadataDebounceRef.current) clearTimeout(metadataDebounceRef.current)
    if (selectedIndicatorNames.length === 0 || selectedCountries.length === 0) return undefined
    metadataDebounceRef.current = setTimeout(() => {
      ensureMetadata(selectedIndicatorNames, selectedCountries)
    }, METADATA_DEBOUNCE_MS)
    return () => clearTimeout(metadataDebounceRef.current)
  }, [selectedIndicatorNames, selectedCountries, ensureMetadata])

  // ---------------------------------------------------------------
  // Indicator availability — same "X/Y countries available" badge as the
  // Explorer's SearchWizard, backed by the same endpoint.
  // ---------------------------------------------------------------
  const availabilityDebounceRef = useRef(null)
  useEffect(() => {
    if (availabilityDebounceRef.current) clearTimeout(availabilityDebounceRef.current)

    if (selectedCountries.length === 0) {
      setIndicatorAvailability({})
      return undefined
    }

    availabilityDebounceRef.current = setTimeout(async () => {
      setLoadingAvailability(true)
      try {
        const { data: response } = await axios.post('/api/v1/data/indicators-for-countries/', { countries: selectedCountries })
        const map = {}
        ;(response.indicators || []).forEach(item => {
          const selectionKey = buildIndicatorSelectionKey({
            id: item.indicator_id,
            foreign_topic: item.topic_id,
            name: item.indicator,
          })
          map[selectionKey] = {
            available: item.available_countries,
            unavailable: item.unavailable_countries,
            has_any_data: item.has_any_data,
          }
        })
        setIndicatorAvailability(map)
      } catch (error) {
        console.error('Error fetching indicator availability:', error)
      } finally {
        setLoadingAvailability(false)
      }
    }, METADATA_DEBOUNCE_MS)

    return () => clearTimeout(availabilityDebounceRef.current)
  }, [selectedCountries])

  // Rows filtered by whichever units/transformations the user left checked
  // for a given indicator's dataset. Falls back to the unfiltered set if the
  // filter would otherwise wipe out all data (mirrors SearchScreen's
  // existing frequency-fallback behavior).
  const filterRowsBySelection = (rows) => {
    const filtered = rows.filter(row => (
      !uncheckedUnits.has(row.unit) && !uncheckedTransformations.has(row.transformation || '')
    ))
    return filtered.length > 0 ? filtered : rows
  }

  // CKAN doesn't guarantee any ordering, so the raw response can front-load
  // an old, sparse ingestion batch (occasionally literal 0 placeholder
  // values). For the preview specifically — not the full download, which
  // keeps every row — we sort by date (most recent first) and cap how many
  // rows come from the same country/unit/transformation combo, so the
  // sample actually reflects what the series looks like today.
  const sampleForPreview = (rows, limit) => {
    const sorted = rows
      .filter(row => row.date)
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
    const seenPerCombo = new Map()
    const sample = []
    for (const row of sorted) {
      if (sample.length >= limit) break
      const comboKey = `${row.country}|${row.unit}|${row.transformation || ''}`
      const seenCount = seenPerCombo.get(comboKey) || 0
      if (seenCount >= 2) continue
      seenPerCombo.set(comboKey, seenCount + 1)
      sample.push(row)
    }
    return sample.length > 0 ? sample : rows.slice(0, limit)
  }

  // ---------------------------------------------------------------
  // Preview — single request for the first selected indicator, capped
  // to a couple of countries, trimmed to a small sample client-side.
  // ---------------------------------------------------------------
  const previewDebounceRef = useRef(null)
  useEffect(() => {
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current)

    if (selectedIndicators.length === 0 || selectedCountries.length === 0) {
      setPreviewRows([])
      return undefined
    }

    previewDebounceRef.current = setTimeout(async () => {
      const indicator = selectedIndicators[0]
      const topicName = topicNameById[indicator.foreign_topic] || ''
      setPreviewLoading(true)
      try {
        const { data: response } = await axios.post('/api/v1/data/change-card-data/', {
          topic: topicName,
          indicator: indicator.name,
          country: selectedCountries.slice(0, PREVIEW_COUNTRY_LIMIT),
          unit: '',
          frequency,
        })
        const rows = response.data_dict || []
        const filtered = filterRowsBySelection(rows).filter(row => (
          (row.frequency || '').toLowerCase().trim() === frequency.toLowerCase().trim()
        ))
        setPreviewRows(sampleForPreview(filtered.length > 0 ? filtered : rows, PREVIEW_ROW_LIMIT))
      } catch (error) {
        console.error('Error fetching preview data:', error)
        setPreviewRows([])
      } finally {
        setPreviewLoading(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, PREVIEW_DEBOUNCE_MS)

    return () => clearTimeout(previewDebounceRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndicators, selectedCountries, frequency, uncheckedUnits, uncheckedTransformations, topicNameById])

  // ---------------------------------------------------------------
  // Metadata tab rows (real data, no fabricated catalogue)
  // ---------------------------------------------------------------
  const metadataRows = useMemo(() => (
    selectedIndicators.map(indicator => {
      const meta = metadataByIndicator[indicator.name] || {}
      const frequencies = Object.keys(meta)
      const units = Array.from(new Set(frequencies.flatMap(freq => Object.keys(meta[freq] || {}))))
      return {
        indicator: indicator.name,
        area: areaNameById[indicator.foreign_area] || '—',
        topic: topicNameById[indicator.foreign_topic] ? normalizeTopicName(topicNameById[indicator.foreign_topic]) : '—',
        units,
        frequencies,
      }
    })
  ), [selectedIndicators, metadataByIndicator, areaNameById, topicNameById])

  // ---------------------------------------------------------------
  // Download — one request per selected indicator, bounded concurrency
  // ---------------------------------------------------------------
  const downloadIndicatorJob = async (indicator, attempt = 0) => {
    const topicName = topicNameById[indicator.foreign_topic] || ''
    try {
      const { data: response } = await axios.post('/api/v1/data/change-card-data/', {
        topic: topicName,
        indicator: indicator.name,
        country: selectedCountries,
        unit: '',
        frequency,
      })
      const rows = response.data_dict || []
      const byFrequency = rows.filter(row => (row.frequency || '').toLowerCase().trim() === frequency.toLowerCase().trim())
      const dataToUse = filterRowsBySelection(byFrequency.length > 0 ? byFrequency : rows)
      return { indicator, rows: dataToUse, error: null }
    } catch (error) {
      if (attempt < 1) {
        return downloadIndicatorJob(indicator, attempt + 1)
      }
      return { indicator, rows: [], error }
    }
  }

  const buildDataset = (results) => {
    if (format === 'xlsx') {
      const workbook = XLSX.utils.book_new()
      const usedNames = new Set()
      results.forEach(({ indicator, rows }) => {
        const sheetRows = rows.map(row => ({
          indicator: indicator.name,
          country: row.country,
          frequency: row.frequency,
          year: row.year || row.period || '',
          date: row.date,
          unit: row.unit,
          effective_unit: row.effective_unit,
          definition: row.definition,
          source: row.source,
          value: row.value,
          transformation: row.transformation || '',
          notes: row.notes || '',
        }))
        const worksheet = XLSX.utils.json_to_sheet(sheetRows)
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetSafeName(indicator.name, usedNames))
      })
      const timestamp = new Date().toISOString().split('T')[0]
      XLSX.writeFile(workbook, `LMW_Data_Center_${timestamp}.xlsx`)
      return
    }

    // CSV / JSON — single long-format dataset, rows ordered by the user's
    // original selection order (not by which request completed first).
    const combinedRows = results.flatMap(({ indicator, rows }) => rows.map(row => ({
      indicator: indicator.name,
      country: row.country,
      frequency: row.frequency,
      year: row.year || row.period || '',
      date: row.date,
      unit: row.unit,
      effective_unit: row.effective_unit,
      definition: row.definition,
      source: row.source,
      value: row.value,
      transformation: row.transformation || '',
      notes: row.notes || '',
    })))

    const timestamp = new Date().toISOString().split('T')[0]
    if (format === 'csv') {
      const worksheet = XLSX.utils.json_to_sheet(combinedRows)
      const csv = XLSX.utils.sheet_to_csv(worksheet)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `LMW_Data_Center_${timestamp}.csv`
      link.click()
      URL.revokeObjectURL(url)
    } else {
      const blob = new Blob([JSON.stringify(combinedRows, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `LMW_Data_Center_${timestamp}.json`
      link.click()
      URL.revokeObjectURL(url)
    }
  }

  const downloadHandler = async () => {
    if (selectedIndicators.length === 0 || selectedCountries.length === 0 || downloading) return

    setDownloading(true)
    setProgress({
      completed: 0,
      total: selectedIndicators.length,
      items: selectedIndicators.map(ind => ({ name: ind.name, status: 'pending', rows: 0 })),
    })

    try {
      const results = await runConcurrent(
        selectedIndicators,
        downloadIndicatorJob,
        DEFAULT_CONCURRENCY,
        (indicator, index, result) => {
          setProgress(prev => {
            if (!prev) return prev
            const items = prev.items.slice()
            items[index] = {
              name: indicator.name,
              status: result && !result.error ? 'done' : 'error',
              rows: result && result.rows ? result.rows.length : 0,
            }
            return { ...prev, completed: prev.completed + 1, items }
          })
        }
      )

      // Results are already ordered exactly as `selectedIndicators` (the
      // runner preserves input order), so the file is deterministic even
      // though the underlying requests resolve in parallel.
      buildDataset(results)
    } catch (error) {
      console.error('Error building dataset:', error)
    } finally {
      setDownloading(false)
    }
  }

  const shareHandler = async () => {
    const params = new URLSearchParams()
    selectedCountries.forEach(c => params.append('country', c))
    selectedIndicatorKeys.forEach(k => params.append('indicator', k))
    params.set('frequency', frequency)
    params.set('from', String(yearFrom))
    params.set('to', String(yearTo))
    params.set('format', format)
    const url = `${window.location.origin}${window.location.pathname}#/data-center?${params.toString()}`
    try {
      await navigator.clipboard.writeText(url)
      setShareMessage('Link copied to clipboard.')
    } catch (error) {
      setShareMessage('Could not copy the link automatically.')
    }
    setTimeout(() => setShareMessage(''), 3000)
  }

  const canDownload = selectedIndicators.length > 0 && selectedCountries.length > 0

  return (
    <div className='landing-page-shell landing-secondary-page'>
      <LandingGlobalHeader
        loadingUser={loadingUser}
        userInfo={userInfo}
        onOpenModal={openModal}
        onLogout={logoutHandler}
      />

      <main>
        <section className='landing-page-hero landing-page-hero-placeholder dc-hero-compact'>
          <div className='container landing-page-hero-inner'>
            <div className='dc-hero-copy'>
              <h1>Build your dataset</h1>
              <p>
                Select countries, indicators, units and periods, review a real sample of what you are about to
                retrieve, and download it in the format your workflow needs.
              </p>
            </div>
            <div className='dc-hero-meta'>
              <span className='dc-hero-pill'><span className='dc-dot'></span> Database updated <strong>{lastUpdateFormatted}</strong></span>
            </div>
          </div>
        </section>

        <div className='landing-section-nav dc-stepnav'>
          <div className='container landing-section-nav-inner'>
            <div className='dc-steps'>
              <a className='dc-step' href='#dcSelect'><span className='dc-step-num'>1</span> Select</a>
              <div className='landing-section-divider'></div>
              <a className='dc-step' href='#dcReview'><span className='dc-step-num'>2</span> Review</a>
              <div className='landing-section-divider'></div>
              <a className='dc-step' href='#dcDownload'><span className='dc-step-num'>3</span> Download</a>
            </div>
            <div className='dc-stepnav-meta'>{selectedIndicators.length} series selected</div>
          </div>
        </div>

        <section className='dc-section' id='dcSelect'>
          <div className='container dc-container'>
            <div className='dc-workbench'>
              <div className='dc-column'>
                <DataCenterCountryPicker
                  countryList={countryList}
                  selectedCountries={selectedCountries}
                  onToggle={toggleCountry}
                  onSelectAll={selectAllCountries}
                  onClear={clearAllCountries}
                />

                <DataCenterIndicatorTree
                  groupedIndicators={groupedIndicators}
                  selectedIndicatorKeys={selectedIndicatorKeys}
                  onToggle={toggleIndicator}
                  selectedCountriesCount={selectedCountries.length}
                  indicatorAvailability={indicatorAvailability}
                  loadingAvailability={loadingAvailability}
                />

                <DataCenterUnitsPanel
                  selectedIndicatorNames={selectedIndicatorNames}
                  metadataByIndicator={metadataByIndicator}
                  frequency={frequency}
                  loading={metadataLoading}
                  uncheckedUnits={uncheckedUnits}
                  uncheckedTransformations={uncheckedTransformations}
                  onToggleUnit={toggleUnit}
                  onToggleTransformation={toggleTransformation}
                />

                <DataCenterPeriodPanel
                  frequency={frequency}
                  onFrequencyChange={setFrequency}
                  yearFrom={yearFrom}
                  yearTo={yearTo}
                  onYearFromChange={setYearFrom}
                  onYearToChange={setYearTo}
                />

                <DataCenterPreviewTable
                  previewRows={previewRows}
                  previewLoading={previewLoading}
                  previewIndicatorName={selectedIndicators[0]?.name || ''}
                  metadataRows={metadataRows}
                />
              </div>

              <div className='dc-column' id='dcDownload'>
                <div className='dc-summary'>
                  <DataCenterSummary
                    countriesCount={selectedCountries.length}
                    indicatorsCount={selectedIndicators.length}
                    seriesCount={selectedIndicators.length}
                    frequency={frequency}
                    yearFrom={yearFrom}
                    yearTo={yearTo}
                    format={format}
                    onFormatChange={setFormat}
                    onDownload={downloadHandler}
                    downloading={downloading}
                    disabled={!canDownload}
                    progress={progress}
                    onShare={shareHandler}
                  />
                  {shareMessage && <p className='dc-inline-note'>{shareMessage}</p>}
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <LandingFooter />
      {authModals}
    </div>
  )
}

export default DataCenterScreen
